import { InputBlock } from '@coast/schemas';
import { ReactElement, ReactNode, useContext, useMemo } from 'react';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { renderLabel as renderDefaultLabel, renderError, renderIcon } from './utils';

// This was measured as the minimum height of the input placeholder + label
// Prevents the vertically centered contents shifting when label is shown/hidden
const MIN_HEIGHT = 60;

interface Props {
  block: InputBlock;
  renderLeft?(): ReactElement;
  renderRight?(block: InputBlock): ReactElement;
  renderLabel?(block: InputBlock): ReactElement;
  onPress?(): void;
  children: ReactNode;
}

export function MBCardInputBlockBase({
  block,
  children,
  renderLeft,
  renderRight,
  onPress,
  renderLabel = renderDefaultLabel,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const { errors } = useCardFormContext();

  const error = errors[block.name];

  const left = useMemo<ReactElement>(
    () => (renderLeft ? renderLeft() : renderIcon(block.icon, block.name)),
    [block, renderLeft],
  );
  const right = useMemo(() => renderRight?.(block), [block, renderRight]);
  const label = useMemo(() => renderLabel?.(block), [block, renderLabel]);

  return (
    <MBTouchableOpacity onPress={onPress} testID={getTestId('card-field-cards', block.name)}>
      <View
        row
        centerVertically
        paddingHorizontal={3}
        paddingVertical={1}
        style={{
          minHeight: MIN_HEIGHT,
          backgroundColor: error ? colors.error.background : 'transparent',
        }}
      >
        {!!left && <View paddingRight={renderContext === 'form' ? 2 : 1}>{left}</View>}
        <View flex>
          {label}
          {children}
        </View>
        {right && <View paddingLeft={2}>{right}</View>}
      </View>
      {renderError(block)}
    </MBTouchableOpacity>
  );
}
